import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DollarSign, Download, Plus, AlertCircle, Wallet, FileText, Archive, Trash2, Edit, CreditCard, BookOpen, Smartphone, FileSignature, Banknote, Upload, X, CheckCircle, Clock, Pencil, CheckCircle2, Circle, Coins, Settings as SettingsIcon, History } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Transaction {
  id: string;
  type: string;
  amount: number;
  category: string;
  description: string;
  transaction_date: string;
  event_id: string | null;
}

interface ExpenseClaim {
  id: string;
  claimant_name: string;
  amount: number;
  description: string;
  receipt_url: string | null;
  receipt_path: string | null;
  status: string;
  event_id: string | null;
  archived_at: string | null;
  created_at: string;
}

interface Event {
  id: string;
  name: string;
  event_date: string;
  status: string;
}

interface CashFloat {
  id: string;
  float_name: string;
  float_location: string | null;
  fifty_pound: number;
  twenty_pound: number;
  ten_pound: number;
  five_pound: number;
  two_pound: number;
  one_pound: number;
  fifty_pence: number;
  twenty_pence: number;
  ten_pence: number;
  five_pence: number;
  two_pence: number;
  one_pence: number;
  status: string;
  event_id: string | null;
  assigned_at: string | null;
  last_counted_at: string | null;
  last_counted_by: string[] | null;
  needs_count: boolean;
  created_at: string;
}

interface FloatCountHistory {
  id: string;
  float_id: string;
  counted_at: string;
  counted_by: string[];
  denominations: any;
  total_amount: number;
  notes: string | null;
  created_at: string;
}

interface Alert {
  id: string;
  title: string;
  due_date: string;
  alert_date: string;
  status: string;
  created_at: string;
}

interface BankAccess {
  id: string;
  member_id: string;
  has_credit_card: boolean;
  has_paying_in_book: boolean;
  has_online_access: boolean;
  is_signatory: boolean;
  has_debit_card: boolean;
  has_cheque_book: boolean;
  member?: {
    full_name: string;
    email: string;
  };
}

interface Member {
  id: string;
  full_name: string;
  email: string;
}

interface TrusteePolicies {
  enabled: boolean;
  approval_threshold: number;
  require_two_counters: boolean;
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  task_type: 'personal' | 'assigned' | 'general';
  assigned_to: string | null;
  created_by: string | null;
  status: 'pending' | 'completed';
  completed_at: string | null;
  created_at: string;
}

interface FinancialYear {
  id: string;
  start_date: string;
  end_date: string;
  is_current: boolean;
  is_archived: boolean;
  created_at: string;
}

interface FundAllocation {
  id: string;
  goal_id: string | null;
  goal_name: string;
  amount: number;
  estimated_spend_date: string;
  description: string | null;
  created_at: string;
}

interface RingFence {
  id: string;
  title: string;
  details: string | null;
  amount: number;
  start_date: string;
  expected_end_date: string | null;
  actual_end_date: string | null;
  goal_id: string | null;
  event_id: string | null;
  status: string;
  created_at: string;
}

export default function TreasurerDashboard() {
  const [currentYear, setCurrentYear] = useState("2024-2025");
  const { toast } = useToast();
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = useState(false);
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [expenseClaims, setExpenseClaims] = useState<ExpenseClaim[]>([]);
  const [archivedClaims, setArchivedClaims] = useState<ExpenseClaim[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState("");
  const [cashFloats, setCashFloats] = useState<CashFloat[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [bankAccessLog, setBankAccessLog] = useState<BankAccess[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [trusteePolicies, setTrusteePolicies] = useState<TrusteePolicies | null>(null);
  const [isFloatDialogOpen, setIsFloatDialogOpen] = useState(false);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
  const [isBankAccessDialogOpen, setIsBankAccessDialogOpen] = useState(false);
  const [isExpenseClaimDialogOpen, setIsExpenseClaimDialogOpen] = useState(false);
  const [editingBankAccess, setEditingBankAccess] = useState<BankAccess | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const [selectedFloat, setSelectedFloat] = useState<CashFloat | null>(null);
  const [selectedCounters, setSelectedCounters] = useState<string[]>([]);
  const [countDate, setCountDate] = useState<string>('');
  const [countNotes, setCountNotes] = useState<string>('');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [floatHistory, setFloatHistory] = useState<FloatCountHistory[]>([]);

  // Allocations state
  const [allocations, setAllocations] = useState<FundAllocation[]>([]);
  const [isAllocationDialogOpen, setIsAllocationDialogOpen] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<FundAllocation | null>(null);
  const [goals, setGoals] = useState<any[]>([]);

  // Reserve amount state
  const [reserveAmount, setReserveAmount] = useState<number>(0);
  const [reserveSettings, setReserveSettings] = useState<any>(null);

  // Tasks state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");

  // Settings state
  const [financialYears, setFinancialYears] = useState<FinancialYear[]>([]);
  const [currentFinancialYear, setCurrentFinancialYear] = useState<FinancialYear | null>(null);
  const [newYearStartMonth, setNewYearStartMonth] = useState("");
  const [newYearStartDay, setNewYearStartDay] = useState("");
  const [newYearEndMonth, setNewYearEndMonth] = useState("");
  const [newYearEndDay, setNewYearEndDay] = useState("");
  const [showArchivedYears, setShowArchivedYears] = useState(false);

  // Edit float state
  const [editingFloat, setEditingFloat] = useState<CashFloat | null>(null);
  const [showEditFloatDialog, setShowEditFloatDialog] = useState(false);

  // Custom report state
  const [showCustomReportDialog, setShowCustomReportDialog] = useState(false);
  const [customReportStart, setCustomReportStart] = useState("");
  const [customReportEnd, setCustomReportEnd] = useState("");

  // PDF download state
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Ring Fences state
  const [ringFences, setRingFences] = useState<RingFence[]>([]);
  const [isRingFenceDialogOpen, setIsRingFenceDialogOpen] = useState(false);
  const [editingRingFence, setEditingRingFence] = useState<RingFence | null>(null);
  
  // PTA ID state
  const [ptaId, setPtaId] = useState<string | null>(null);

  // Edit expense claim state
  const [editingExpenseClaim, setEditingExpenseClaim] = useState<ExpenseClaim | null>(null);
  const [isEditExpenseClaimDialogOpen, setIsEditExpenseClaimDialogOpen] = useState(false);

  // Edit transaction state
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [isEditTransactionDialogOpen, setIsEditTransactionDialogOpen] = useState(false);
  const [isDeleteTransactionDialogOpen, setIsDeleteTransactionDialogOpen] = useState(false);
  const [deletingTransactionId, setDeletingTransactionId] = useState<string | null>(null);
  const [deleteTransactionReason, setDeleteTransactionReason] = useState("");
  const [editTransactionCategory, setEditTransactionCategory] = useState("");

  // Edit alert state
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);
  const [isEditAlertDialogOpen, setIsEditAlertDialogOpen] = useState(false);

  // Edit task state
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] = useState(false);

  // Current user ID for task ownership check
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const categories = ["Event", "Fundraising", "Facilities", "Supplies", "Equipment", "Catering", "Other"];
  const presetAlerts = [
    { title: "Insurance Renewal", daysBeforeAlert: 30 },
    { title: "Gambling Licence Renewal", daysBeforeAlert: 30 },
    { title: "Media Licence Renewal", daysBeforeAlert: 30 },
    { title: "PTA Hub Renewal", daysBeforeAlert: 30 },
  ];

  useEffect(() => {
    fetchPtaId();
    fetchCurrentUserId();
  }, []);

  // Fetch data after ptaId is available
  useEffect(() => {
    if (ptaId) {
      fetchTransactions();
      fetchExpenseClaims();
      fetchEvents();
      fetchCashFloats();
      fetchAlerts();
      fetchBankAccessLog();
      fetchMembers();
      fetchTrusteePolicies();
      fetchTasks();
      fetchFinancialYears();
      fetchAllocations();
      fetchGoals();
      fetchReserveSettings();
      fetchAllFloatHistory();
    }
  }, [ptaId]);

  const fetchCurrentUserId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setCurrentUserId(user.id);
  };

  const fetchAllFloatHistory = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('float_count_history')
      .select('*')
      .eq('pta_id', ptaId)
      .order('counted_at', { ascending: false });
    
    if (!error && data) {
      setFloatHistory(data);
    }
  };
  
  useEffect(() => {
    if (ptaId) {
      fetchRingFences();
    }
  }, [ptaId]);

  const fetchTransactions = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('pta_id', ptaId)
      .order('transaction_date', { ascending: false });
    
    if (!error && data) setTransactions(data);
  };

  const fetchExpenseClaims = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('expense_claims')
      .select('*')
      .eq('pta_id', ptaId)
      .neq('status', 'archived')
      .order('created_at', { ascending: false });
    
    if (!error && data) {
      // Fetch profiles for all submitters
      const submitterIds = [...new Set(data.map(claim => claim.submitted_by).filter(Boolean))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, display_name, email')
        .in('id', submitterIds);
      
      // Create a map of user_id to profile
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      
      // Merge profile data with claims
      const claimsWithNames = data.map((claim: any) => {
        const profile = profileMap.get(claim.submitted_by);
        return {
          ...claim,
          claimant_name: profile?.full_name || profile?.display_name || profile?.email || claim.claimant_name || 'Unknown'
        };
      });
      
      setExpenseClaims(claimsWithNames as any);
    }

    const { data: archived } = await supabase
      .from('expense_claims')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'archived')
      .order('archived_at', { ascending: false });
    
    if (archived) {
      // Fetch profiles for archived claims too
      const submitterIds = [...new Set(archived.map(claim => claim.submitted_by).filter(Boolean))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, display_name, email')
        .in('id', submitterIds);
      
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      
      const archivedWithNames = archived.map((claim: any) => {
        const profile = profileMap.get(claim.submitted_by);
        return {
          ...claim,
          claimant_name: profile?.full_name || profile?.display_name || profile?.email || claim.claimant_name || 'Unknown'
        };
      });
      
      setArchivedClaims(archivedWithNames as any);
    }
  };

  const fetchEvents = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('event_date', { ascending: true });
    
    if (!error && data) setEvents(data);
  };

  const fetchCashFloats = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('cash_floats')
      .select('*')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false });
    
    if (!error && data) setCashFloats(data);
  };

  const fetchAlerts = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('alerts')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('alert_date', { ascending: true });
    
    if (!error && data) setAlerts(data);
  };

  const fetchBankAccessLog = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('bank_access_log')
      .select('*, member:members(full_name, email)')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false});

    if (!error && data) setBankAccessLog(data as any);
  };

  const fetchMembers = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('members')
      .select('id, full_name, email')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('full_name', { ascending: true });
    
    if (!error && data) setMembers(data);
  };

  const fetchTrusteePolicies = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('trustee_policies')
      .select('*')
      .eq('pta_id', ptaId)
      .maybeSingle();

    if (!error && data) {
      setTrusteePolicies(data);
    }
  };

  const fetchTasks = async () => {
    if (!ptaId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('assigned_to', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) setTasks(data as Task[]);
  };

  const fetchFinancialYears = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('financial_years')
      .select('*')
      .eq('pta_id', ptaId)
      .order('start_date', { ascending: false });

    if (!error && data) {
      setFinancialYears(data);
      const current = data.find(y => y.is_current);
      if (current) setCurrentFinancialYear(current);
    }
  };

  const fetchAllocations = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('fund_allocations')
      .select('*')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false });
    
    if (!error && data) setAllocations(data);
  };

  const fetchGoals = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .order('title', { ascending: true });
    
    if (!error && data) setGoals(data);
  };

  const fetchReserveSettings = async () => {
    if (!ptaId) return;
    const { data, error } = await supabase
      .from('treasurer_settings')
      .select('*')
      .eq('pta_id', ptaId)
      .maybeSingle();
    
    if (data) {
      setReserveSettings(data);
      setReserveAmount(data.reserve_amount);
    }
  };

  const fetchPtaId = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();
    
    if (!error && data) setPtaId(data.pta_id);
  };

  const fetchRingFences = async () => {
    if (!ptaId) return;
    
    const { data, error } = await supabase
      .from('ring_fences')
      .select('*')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false });
    
    if (!error && data) setRingFences(data);
  };
  
  const handleAddTransaction = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const category = selectedCategory === "Other" ? customCategory : selectedCategory;
    const eventId = formData.get('event_id') as string;
    
    const { error } = await supabase
      .from('transactions')
      .insert([{
        type: formData.get('type') as string,
        amount: parseFloat(formData.get('amount') as string),
        category: category,
        description: formData.get('description') as string,
        event_id: eventId === 'none' || !eventId ? null : eventId,
        transaction_date: formData.get('date') as string,
        pta_id: ptaId,
      }]);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Transaction added",
        description: "The transaction has been recorded successfully.",
      });
      setIsTransactionDialogOpen(false);
      setSelectedCategory("");
      setShowCustomCategory(false);
      setCustomCategory("");
      fetchTransactions();
    }
  };

  const handleEditTransaction = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingTransaction) return;

    const formData = new FormData(e.currentTarget);
    const category = editTransactionCategory === "Other" ? formData.get('custom_category') as string : editTransactionCategory;
    const eventId = formData.get('event_id') as string;
    
    const { error } = await supabase
      .from('transactions')
      .update({
        type: formData.get('type') as string,
        amount: parseFloat(formData.get('amount') as string),
        category: category,
        description: formData.get('description') as string,
        event_id: eventId === 'none' || !eventId ? null : eventId,
        transaction_date: formData.get('date') as string,
      })
      .eq('id', editingTransaction.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Transaction updated",
        description: "The transaction has been updated successfully.",
      });
      setIsEditTransactionDialogOpen(false);
      setEditingTransaction(null);
      setEditTransactionCategory("");
      fetchTransactions();
    }
  };

  const handleDeleteTransaction = async () => {
    if (!deletingTransactionId) return;

    // Log the deletion reason (in a real app, you might store this)
    console.log('Transaction deleted with reason:', deleteTransactionReason);

    const { error } = await supabase
      .from('transactions')
      .delete()
      .eq('id', deletingTransactionId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Transaction deleted",
        description: "The transaction has been deleted.",
      });
      setIsDeleteTransactionDialogOpen(false);
      setDeletingTransactionId(null);
      setDeleteTransactionReason("");
      fetchTransactions();
    }
  };
  
  const generatePDF = (reportType: string, startDate?: string, endDate?: string) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(20);
    doc.setTextColor(40, 40, 40);
    doc.text("PTA Treasurer Report", pageWidth / 2, 20, { align: "center" });
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    const reportTitle = reportType === 'custom' 
      ? `Custom Report: ${startDate} to ${endDate}`
      : `${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report`;
    doc.text(reportTitle, pageWidth / 2, 30, { align: "center" });
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 37, { align: "center" });
    
    // Filter transactions by date range
    let filteredTransactions = transactions;
    if (startDate && endDate) {
      filteredTransactions = transactions.filter(t => {
        const tDate = new Date(t.transaction_date);
        return tDate >= new Date(startDate) && tDate <= new Date(endDate);
      });
    } else if (reportType === 'monthly') {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      filteredTransactions = transactions.filter(t => new Date(t.transaction_date) >= lastMonth);
    } else if (reportType === 'yearly') {
      const lastYear = new Date();
      lastYear.setFullYear(lastYear.getFullYear() - 1);
      filteredTransactions = transactions.filter(t => new Date(t.transaction_date) >= lastYear);
    }
    
    // Summary
    const totalIncome = filteredTransactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = filteredTransactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    const netBalance = totalIncome - totalExpenses;
    
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Summary", 14, 50);
    doc.setFontSize(10);
    doc.text(`Total Income: £${totalIncome.toFixed(2)}`, 14, 58);
    doc.text(`Total Expenses: £${totalExpenses.toFixed(2)}`, 14, 65);
    doc.setFont(undefined, 'bold');
    doc.text(`Net Balance: £${netBalance.toFixed(2)}`, 14, 72);
    doc.setFont(undefined, 'normal');
    
    // Transactions table
    const tableData = filteredTransactions.map(t => [
      new Date(t.transaction_date).toLocaleDateString(),
      t.type.charAt(0).toUpperCase() + t.type.slice(1),
      t.category,
      t.description,
      `£${t.amount.toFixed(2)}`
    ]);
    
    autoTable(doc, {
      head: [['Date', 'Type', 'Category', 'Description', 'Amount']],
      body: tableData,
      startY: 80,
      theme: 'grid',
      headStyles: { fillColor: [66, 66, 66] },
      styles: { fontSize: 9 },
    });
    
    // Footer
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
    }
    
    const fileName = `${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    toast({
      title: "Report generated",
      description: `${reportTitle} has been downloaded.`,
    });
  };
  
  const handleExportCSV = () => {
    const incomeRows = transactions
      .filter(t => t.type === 'income')
      .map(t => [
        new Date(t.transaction_date).toLocaleDateString(),
        'Income',
        t.category,
        t.description,
        t.amount.toFixed(2)
      ]);
    
    const expenseRows = transactions
      .filter(t => t.type === 'expense')
      .map(t => [
        new Date(t.transaction_date).toLocaleDateString(),
        'Expense',
        t.category,
        t.description,
        t.amount.toFixed(2)
      ]);

    const headers = ["Date", "Type", "Category", "Description", "Amount"];
    const csvContent = [
      headers.join(","),
      ...incomeRows.map(row => row.join(",")),
      ...expenseRows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `transactions_${currentYear}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Export complete",
      description: "Transactions have been exported to CSV.",
    });
  };

  const handleAddExpenseClaim = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to submit an expense claim.",
        variant: "destructive",
      });
      return;
    }
    
    let receiptPath = null;
    let receiptUrl = null;

    if (receiptFile) {
      const fileName = `${Date.now()}_${receiptFile.name}`;
      const { data, error: uploadError } = await supabase.storage
        .from('receipts')
        .upload(fileName, receiptFile);

      if (uploadError) {
        toast({
          title: "Error",
          description: "Failed to upload receipt: " + uploadError.message,
          variant: "destructive",
        });
        return;
      }

      receiptPath = data.path;
      const { data: urlData } = supabase.storage
        .from('receipts')
        .getPublicUrl(receiptPath);
      receiptUrl = urlData.publicUrl;
    }

    const eventId = formData.get('event_id') as string;

    const { error } = await supabase
      .from('expense_claims')
      .insert([{
        claimant_name: formData.get('claimant_name') as string,
        amount: parseFloat(formData.get('amount') as string),
        description: formData.get('description') as string,
        event_id: eventId === 'none' || !eventId ? null : eventId,
        receipt_path: receiptPath,
        receipt_url: receiptUrl,
        status: 'pending',
        submitted_by: user.id,
        pta_id: ptaId,
      }]);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Expense claim submitted",
        description: "The claim has been submitted for approval.",
      });
      setIsExpenseClaimDialogOpen(false);
      setReceiptFile(null);
      fetchExpenseClaims();
    }
  };

  const handleApproveClaim = async (claimId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Find the claim to check its amount
    const claim = expenseClaims.find(c => c.id === claimId);
    if (!claim) return;

    // Check if claim exceeds trustee policy threshold
    const requiresTrusteeApproval = trusteePolicies?.enabled && 
      claim.amount > trusteePolicies.approval_threshold;

    const newStatus = requiresTrusteeApproval ? 'waiting_approval' : 'approved';

    const { error } = await supabase
      .from('expense_claims')
      .update({ 
        status: newStatus, 
        approved_at: new Date().toISOString(),
        approved_by: user.id
      })
      .eq('id', claimId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: requiresTrusteeApproval ? "Sent for trustee approval" : "Claim approved",
        description: requiresTrusteeApproval 
          ? "This claim exceeds the threshold and requires trustee approval."
          : "The expense claim has been approved.",
      });
      fetchExpenseClaims();
    }
  };

  const handleMarkPaid = async (claimId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('expense_claims')
      .update({ 
        status: 'paid', 
        paid_at: new Date().toISOString(),
        paid_by: user.id
      })
      .eq('id', claimId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Claim marked as paid",
        description: "The expense claim has been marked as paid.",
      });
      fetchExpenseClaims();
    }
  };

  const handleArchiveClaim = async (claimId: string) => {
    const { error } = await supabase
      .from('expense_claims')
      .update({ status: 'archived', archived_at: new Date().toISOString() })
      .eq('id', claimId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Claim archived",
        description: "The expense claim has been archived.",
      });
      fetchExpenseClaims();
    }
  };

  const handleEditExpenseClaim = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingExpenseClaim) return;

    const formData = new FormData(e.currentTarget);
    
    const { error } = await supabase
      .from('expense_claims')
      .update({
        description: formData.get('description') as string,
        amount: parseFloat(formData.get('amount') as string),
        event_id: formData.get('event_id') === 'none' ? null : formData.get('event_id') as string,
      })
      .eq('id', editingExpenseClaim.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Claim updated",
        description: "The expense claim has been updated.",
      });
      setIsEditExpenseClaimDialogOpen(false);
      setEditingExpenseClaim(null);
      fetchExpenseClaims();
    }
  };

  const handleDeleteExpenseClaim = async (claimId: string) => {
    if (!confirm('Are you sure you want to delete this expense claim? This action cannot be undone.')) {
      return;
    }

    const { error } = await supabase
      .from('expense_claims')
      .delete()
      .eq('id', claimId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Claim deleted",
        description: "The expense claim has been deleted.",
      });
      fetchExpenseClaims();
    }
  };

  const handleCreateFloat = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const denominations = {
      fifty_pound: parseInt(formData.get('fifty_pound') as string) || 0,
      twenty_pound: parseInt(formData.get('twenty_pound') as string) || 0,
      ten_pound: parseInt(formData.get('ten_pound') as string) || 0,
      five_pound: parseInt(formData.get('five_pound') as string) || 0,
      two_pound: parseInt(formData.get('two_pound') as string) || 0,
      one_pound: parseInt(formData.get('one_pound') as string) || 0,
      fifty_pence: parseInt(formData.get('fifty_pence') as string) || 0,
      twenty_pence: parseInt(formData.get('twenty_pence') as string) || 0,
      ten_pence: parseInt(formData.get('ten_pence') as string) || 0,
      five_pence: parseInt(formData.get('five_pence') as string) || 0,
      two_pence: parseInt(formData.get('two_pence') as string) || 0,
      one_pence: parseInt(formData.get('one_pence') as string) || 0,
    };

    const totalAmount = 
      denominations.fifty_pound * 50 +
      denominations.twenty_pound * 20 +
      denominations.ten_pound * 10 +
      denominations.five_pound * 5 +
      denominations.two_pound * 2 +
      denominations.one_pound * 1 +
      denominations.fifty_pence * 0.50 +
      denominations.twenty_pence * 0.20 +
      denominations.ten_pence * 0.10 +
      denominations.five_pence * 0.05 +
      denominations.two_pence * 0.02 +
      denominations.one_pence * 0.01;
    
    const floatData = {
      float_name: formData.get('float_name') as string,
      float_location: formData.get('float_location') as string,
      amount: totalAmount,
      ...denominations,
      status: 'available',
      pta_id: ptaId,
    };

    const { error } = await supabase
      .from('cash_floats')
      .insert([floatData]);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to create cash float: " + error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Cash float created",
        description: "The cash float has been created successfully.",
      });
      setIsFloatDialogOpen(false);
      fetchCashFloats();
    }
  };

  const handleUpdateFloat = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingFloat) return;

    const formData = new FormData(e.currentTarget);
    
    const denominations = {
      fifty_pound: parseInt(formData.get('fifty_pound') as string) || 0,
      twenty_pound: parseInt(formData.get('twenty_pound') as string) || 0,
      ten_pound: parseInt(formData.get('ten_pound') as string) || 0,
      five_pound: parseInt(formData.get('five_pound') as string) || 0,
      two_pound: parseInt(formData.get('two_pound') as string) || 0,
      one_pound: parseInt(formData.get('one_pound') as string) || 0,
      fifty_pence: parseInt(formData.get('fifty_pence') as string) || 0,
      twenty_pence: parseInt(formData.get('twenty_pence') as string) || 0,
      ten_pence: parseInt(formData.get('ten_pence') as string) || 0,
      five_pence: parseInt(formData.get('five_pence') as string) || 0,
      two_pence: parseInt(formData.get('two_pence') as string) || 0,
      one_pence: parseInt(formData.get('one_pence') as string) || 0,
    };

    const totalAmount = 
      denominations.fifty_pound * 50 +
      denominations.twenty_pound * 20 +
      denominations.ten_pound * 10 +
      denominations.five_pound * 5 +
      denominations.two_pound * 2 +
      denominations.one_pound * 1 +
      denominations.fifty_pence * 0.50 +
      denominations.twenty_pence * 0.20 +
      denominations.ten_pence * 0.10 +
      denominations.five_pence * 0.05 +
      denominations.two_pence * 0.02 +
      denominations.one_pence * 0.01;

    const { error } = await supabase
      .from('cash_floats')
      .update({
        float_name: formData.get('float_name') as string,
        float_location: formData.get('float_location') as string,
        amount: totalAmount,
        ...denominations,
      })
      .eq('id', editingFloat.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update cash float: " + error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Cash float updated",
        description: "The cash float has been updated successfully.",
      });
      setShowEditFloatDialog(false);
      setEditingFloat(null);
      fetchCashFloats();
    }
  };

  const handleDeleteFloat = async (floatId: string) => {
    const { error } = await supabase
      .from('cash_floats')
      .delete()
      .eq('id', floatId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Cash float deleted",
        description: "The cash float has been deleted.",
      });
      fetchCashFloats();
    }
  };

  const handleAddAlert = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const { error } = await supabase
      .from('alerts')
      .insert([{
        title: formData.get('title') as string,
        due_date: formData.get('due_date') as string,
        alert_date: formData.get('alert_date') as string,
        status: 'active',
        pta_id: ptaId,
      }]);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Alert created",
        description: "The alert has been created successfully.",
      });
      setIsAlertDialogOpen(false);
      fetchAlerts();
    }
  };

  const handleEditAlert = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingAlert) return;

    const formData = new FormData(e.currentTarget);
    
    const { error } = await supabase
      .from('alerts')
      .update({
        title: formData.get('title') as string,
        due_date: formData.get('due_date') as string,
        alert_date: formData.get('alert_date') as string,
      })
      .eq('id', editingAlert.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Alert updated",
        description: "The alert has been updated successfully.",
      });
      setIsEditAlertDialogOpen(false);
      setEditingAlert(null);
      fetchAlerts();
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    const { error } = await supabase
      .from('alerts')
      .update({ status: 'deleted' })
      .eq('id', alertId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Alert deleted",
        description: "The alert has been deleted.",
      });
      fetchAlerts();
    }
  };

  const handleAddBankAccess = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const bankAccessData = {
      member_id: formData.get('member_id') as string,
      has_credit_card: formData.get('has_credit_card') === 'on',
      has_paying_in_book: formData.get('has_paying_in_book') === 'on',
      has_online_access: formData.get('has_online_access') === 'on',
      is_signatory: formData.get('is_signatory') === 'on',
      has_debit_card: formData.get('has_debit_card') === 'on',
      has_cheque_book: formData.get('has_cheque_book') === 'on',
      pta_id: ptaId,
    };

    const { error } = await supabase
      .from('bank_access_log')
      .insert([bankAccessData]);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to add bank access: " + error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Bank access added",
        description: "The bank access log has been updated.",
      });
      setIsBankAccessDialogOpen(false);
      fetchBankAccessLog();
    }
  };

  const handleUpdateBankAccess = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingBankAccess) return;

    const formData = new FormData(e.currentTarget);
    
    const bankAccessData = {
      member_id: formData.get('member_id') as string,
      has_credit_card: formData.get('has_credit_card') === 'on',
      has_paying_in_book: formData.get('has_paying_in_book') === 'on',
      has_online_access: formData.get('has_online_access') === 'on',
      is_signatory: formData.get('is_signatory') === 'on',
      has_debit_card: formData.get('has_debit_card') === 'on',
      has_cheque_book: formData.get('has_cheque_book') === 'on',
    };

    const { error } = await supabase
      .from('bank_access_log')
      .update(bankAccessData)
      .eq('id', editingBankAccess.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update bank access: " + error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Bank access updated",
        description: "The bank access log has been updated.",
      });
      setIsBankAccessDialogOpen(false);
      setEditingBankAccess(null);
      fetchBankAccessLog();
    }
  };

  const handleDeleteBankAccess = async (id: string) => {
    const { error } = await supabase
      .from('bank_access_log')
      .delete()
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Bank access deleted",
        description: "The bank access log entry has been deleted.",
      });
      fetchBankAccessLog();
    }
  };

  const openCountDialog = (cashFloat: CashFloat) => {
    setSelectedFloat(cashFloat);
    setSelectedCounters([]);
    setCountDate(new Date().toISOString().split('T')[0]);
    setCountNotes('');
    setCountDialogOpen(true);
  };

  const openHistoryDialog = async (cashFloat: CashFloat) => {
    setSelectedFloat(cashFloat);
    
    const { data, error } = await supabase
      .from('float_count_history')
      .select('*')
      .eq('float_id', cashFloat.id)
      .order('counted_at', { ascending: false });
    
    if (!error && data) {
      setFloatHistory(data);
    }
    setHistoryDialogOpen(true);
  };

  const handleSaveCount = async () => {
    if (!selectedFloat) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to record a count.",
        variant: "destructive",
      });
      return;
    }

    if (trusteePolicies?.require_two_counters && selectedCounters.length < 2) {
      toast({
        title: "Error",
        description: "Policy requires at least 2 counters",
        variant: "destructive",
      });
      return;
    }

    if (!countDate) {
      toast({
        title: "Error",
        description: "Please select a count date",
        variant: "destructive",
      });
      return;
    }

    // Calculate total amount
    const totalAmount = 
      selectedFloat.fifty_pound * 50 +
      selectedFloat.twenty_pound * 20 +
      selectedFloat.ten_pound * 10 +
      selectedFloat.five_pound * 5 +
      selectedFloat.two_pound * 2 +
      selectedFloat.one_pound * 1 +
      selectedFloat.fifty_pence * 0.50 +
      selectedFloat.twenty_pence * 0.20 +
      selectedFloat.ten_pence * 0.10 +
      selectedFloat.five_pence * 0.05 +
      selectedFloat.two_pence * 0.02 +
      selectedFloat.one_pence * 0.01;

    // Ensure the current user's ID is included in counted_by for RLS
    const countedByWithUser = selectedCounters.includes(user.id) 
      ? selectedCounters 
      : [user.id, ...selectedCounters];

    // Insert into history
    const { error: historyError } = await supabase
      .from('float_count_history')
      .insert([{
        float_id: selectedFloat.id,
        counted_at: countDate,
        counted_by: countedByWithUser,
        denominations: {
          fifty_pound: selectedFloat.fifty_pound,
          twenty_pound: selectedFloat.twenty_pound,
          ten_pound: selectedFloat.ten_pound,
          five_pound: selectedFloat.five_pound,
          two_pound: selectedFloat.two_pound,
          one_pound: selectedFloat.one_pound,
          fifty_pence: selectedFloat.fifty_pence,
          twenty_pence: selectedFloat.twenty_pence,
          ten_pence: selectedFloat.ten_pence,
          five_pence: selectedFloat.five_pence,
          two_pence: selectedFloat.two_pence,
          one_pence: selectedFloat.one_pence,
        },
        total_amount: totalAmount,
        notes: countNotes || null,
        pta_id: ptaId,
      }]);

    if (historyError) {
      toast({
        title: "Error",
        description: historyError.message,
        variant: "destructive",
      });
      return;
    }

    // Update cash float
    const { error } = await supabase
      .from('cash_floats')
      .update({
        last_counted_at: countDate,
        last_counted_by: selectedCounters,
        needs_count: false,
      })
      .eq('id', selectedFloat.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Count recorded",
        description: "The cash float count has been recorded.",
      });
      setCountDialogOpen(false);
      setSelectedFloat(null);
      setSelectedCounters([]);
      setCountDate('');
      setCountNotes('');
      fetchCashFloats();
      fetchAllFloatHistory();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setReceiptFile(files[0]);
    }
  };

  // Task handlers
  const handleAddTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('tasks')
      .insert([{
        title: newTaskTitle,
        description: newTaskDescription,
        task_type: 'personal',
        assigned_to: user.id,
        created_by: user.id,
        status: 'pending',
        pta_id: ptaId,
      }]);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Task added",
        description: "Your task has been created.",
      });
      setNewTaskTitle("");
      setNewTaskDescription("");
      fetchTasks();
    }
  };

  const handleEditTask = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingTask) return;

    const formData = new FormData(e.currentTarget);
    
    const { error } = await supabase
      .from('tasks')
      .update({
        title: formData.get('title') as string,
        description: formData.get('description') as string || null,
      })
      .eq('id', editingTask.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Task updated",
        description: "The task has been updated successfully.",
      });
      setIsEditTaskDialogOpen(false);
      setEditingTask(null);
      fetchTasks();
    }
  };

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
    const { error } = await supabase
      .from('tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
      })
      .eq('id', taskId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      fetchTasks();
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Task deleted",
        description: "The task has been removed.",
      });
      fetchTasks();
    }
  };

  const handleClearCompletedTasks = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const completedPersonalTasks = tasks.filter(
      t => t.status === 'completed' && t.task_type === 'personal' && t.created_by === user.id
    );

    if (completedPersonalTasks.length === 0) {
      toast({
        title: "No tasks to clear",
        description: "There are no completed personal tasks to remove.",
      });
      return;
    }

    const { error } = await supabase
      .from('tasks')
      .delete()
      .in('id', completedPersonalTasks.map(t => t.id));

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Tasks cleared",
        description: `${completedPersonalTasks.length} completed task(s) removed.`,
      });
      fetchTasks();
    }
  };

  // Financial year handlers
  const handleUpdateFinancialYear = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!currentFinancialYear) return;

    // Parse current dates to get years
    const currentStart = new Date(currentFinancialYear.start_date);
    const currentEnd = new Date(currentFinancialYear.end_date);
    const startYear = currentStart.getFullYear();
    const endYear = currentEnd.getFullYear();

    // Construct new dates using the same years but new month/day
    const newStartDate = `${startYear}-${newYearStartMonth.padStart(2, '0')}-${newYearStartDay.padStart(2, '0')}`;
    const newEndDate = `${endYear}-${newYearEndMonth.padStart(2, '0')}-${newYearEndDay.padStart(2, '0')}`;

    const { error } = await supabase
      .from('financial_years')
      .update({
        start_date: newStartDate,
        end_date: newEndDate,
      })
      .eq('id', currentFinancialYear.id);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Financial year updated",
        description: "The financial year dates have been updated.",
      });
      setNewYearStartMonth("");
      setNewYearStartDay("");
      setNewYearEndMonth("");
      setNewYearEndDay("");
      fetchFinancialYears();
    }
  };

  const handleAddAllocation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const goalId = formData.get('goal_id') as string;
    const { error } = await supabase.from('fund_allocations').insert({
      goal_id: goalId === 'none' ? null : goalId || null,
      goal_name: formData.get('goal_name') as string,
      amount: parseFloat(formData.get('amount') as string),
      estimated_spend_date: formData.get('estimated_spend_date') as string,
      description: formData.get('description') as string,
      pta_id: ptaId,
    });

    if (!error) {
      toast({ title: "Allocation added successfully" });
      setIsAllocationDialogOpen(false);
      fetchAllocations();
    }
  };

  const handleDeleteAllocation = async (id: string) => {
    await supabase.from('fund_allocations').delete().eq('id', id);
    fetchAllocations();
  };

  const handleUpdateReserve = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const amount = parseFloat(formData.get('reserve_amount') as string);

    if (reserveSettings) {
      await supabase.from('treasurer_settings').update({ reserve_amount: amount }).eq('id', reserveSettings.id);
    } else {
      await supabase.from('treasurer_settings').insert({ reserve_amount: amount, pta_id: ptaId });
    }
    
    toast({ title: "Reserve amount updated" });
    fetchReserveSettings();
  };

  const handleStartNewYear = async () => {
    // Check for outstanding expenses
    const outstandingExpenses = expenseClaims.filter(
      c => c.status === 'approved' || c.status === 'pending'
    );

    if (outstandingExpenses.length > 0) {
      toast({
        title: "Cannot start new year",
        description: `There are ${outstandingExpenses.length} outstanding expense(s) to be paid.`,
        variant: "destructive",
      });
      return;
    }

    if (!currentFinancialYear) return;

    // Archive current year
    const { error: archiveError } = await supabase
      .from('financial_years')
      .update({ is_current: false, is_archived: true })
      .eq('id', currentFinancialYear.id);

    if (archiveError) {
      toast({
        title: "Error",
        description: archiveError.message,
        variant: "destructive",
      });
      return;
    }

    // Create new year
    const currentEnd = new Date(currentFinancialYear.end_date);
    const newStart = new Date(currentEnd);
    newStart.setDate(newStart.getDate() + 1);
    const newEnd = new Date(newStart);
    newEnd.setFullYear(newEnd.getFullYear() + 1);
    newEnd.setDate(newEnd.getDate() - 1);

    const { error: newYearError } = await supabase
      .from('financial_years')
      .insert([{
        start_date: newStart.toISOString().split('T')[0],
        end_date: newEnd.toISOString().split('T')[0],
        is_current: true,
        pta_id: ptaId,
        is_archived: false,
      }]);

    if (newYearError) {
      toast({
        title: "Error",
        description: newYearError.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "New year started",
        description: "A new financial year has been created.",
      });
      fetchFinancialYears();
    }
  };

  // Ring Fences handlers
  const handleAddRingFence = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!ptaId) {
      toast({ title: "Error", description: "PTA ID not found", variant: "destructive" });
      return;
    }
    
    const formData = new FormData(e.currentTarget);
    
    const goalId = formData.get('goal_id') as string;
    const eventId = formData.get('event_id') as string;
    const { error } = await supabase.from('ring_fences').insert({
      pta_id: ptaId,
      title: formData.get('title') as string,
      details: formData.get('details') as string || null,
      amount: parseFloat(formData.get('amount') as string),
      start_date: formData.get('start_date') as string,
      expected_end_date: formData.get('expected_end_date') as string || null,
      goal_id: goalId === 'none' ? null : goalId || null,
      event_id: eventId === 'none' ? null : eventId || null,
      status: 'active',
    });

    if (!error) {
      toast({ title: "Ring fence created successfully" });
      setIsRingFenceDialogOpen(false);
      fetchRingFences();
    } else {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleUpdateRingFence = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingRingFence) return;

    const formData = new FormData(e.currentTarget);
    
    const goalId = formData.get('goal_id') as string;
    const eventId = formData.get('event_id') as string;
    const { error } = await supabase
      .from('ring_fences')
      .update({
        title: formData.get('title') as string,
        details: formData.get('details') as string || null,
        amount: parseFloat(formData.get('amount') as string),
        start_date: formData.get('start_date') as string,
        expected_end_date: formData.get('expected_end_date') as string || null,
        actual_end_date: formData.get('actual_end_date') as string || null,
        goal_id: goalId === 'none' ? null : goalId || null,
        event_id: eventId === 'none' ? null : eventId || null,
        status: formData.get('status') as string,
      })
      .eq('id', editingRingFence.id);

    if (!error) {
      toast({ title: "Ring fence updated successfully" });
      setEditingRingFence(null);
      setIsRingFenceDialogOpen(false);
      fetchRingFences();
    } else {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const handleDeleteRingFence = async (id: string) => {
    if (!confirm("Are you sure you want to delete this ring fence?")) return;
    
    const { error } = await supabase.from('ring_fences').delete().eq('id', id);
    
    if (!error) {
      toast({ title: "Ring fence deleted" });
      fetchRingFences();
    }
  };

  const generateAGMReport = async () => {
    setIsGeneratingPDF(true);
    try {
      const doc = new jsPDF();
      let yPos = 20;

      // Header
      doc.setFontSize(20);
      doc.text("Treasurer's Report for AGM", 105, yPos, { align: "center" });
      yPos += 10;
      
      doc.setFontSize(12);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, yPos, { align: "center" });
      yPos += 15;

      // Financial Summary
      const totalIncome = transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
      const totalExpenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
      const netPosition = totalIncome - totalExpenses;
      const totalRingFenced = ringFences.filter(rf => rf.status === 'active').reduce((sum, rf) => sum + rf.amount, 0);
      const availableFunds = netPosition - totalRingFenced - reserveAmount;

      doc.setFontSize(14);
      doc.text("Financial Summary", 14, yPos);
      yPos += 8;
      doc.setFontSize(11);
      doc.text(`Total Income: £${totalIncome.toFixed(2)}`, 14, yPos);
      yPos += 6;
      doc.text(`Total Expenses: £${totalExpenses.toFixed(2)}`, 14, yPos);
      yPos += 6;
      doc.text(`Net Position: £${netPosition.toFixed(2)}`, 14, yPos);
      yPos += 6;
      doc.text(`Reserve Amount: £${reserveAmount.toFixed(2)}`, 14, yPos);
      yPos += 6;
      doc.text(`Ring Fenced: £${totalRingFenced.toFixed(2)}`, 14, yPos);
      yPos += 6;
      doc.setFont(undefined, 'bold');
      doc.text(`Available Funds: £${availableFunds.toFixed(2)}`, 14, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 15;

      // Event Breakdown
      if (events.length > 0) {
        doc.setFontSize(14);
        doc.text("Event Income Breakdown", 14, yPos);
        yPos += 8;
        
        const eventIncomeData = events.map(event => {
          const eventIncome = transactions.filter(t => t.event_id === event.id && t.type === 'income')
            .reduce((sum, t) => sum + t.amount, 0);
          return [event.name, `£${eventIncome.toFixed(2)}`];
        }).filter(([_, amount]) => amount !== '£0.00');

        if (eventIncomeData.length > 0) {
          autoTable(doc, {
            startY: yPos,
            head: [['Event', 'Income']],
            body: eventIncomeData,
            theme: 'striped',
            headStyles: { fillColor: [59, 130, 246] },
          });
          yPos = (doc as any).lastAutoTable.finalY + 15;
        }
      }

      // Goals Status
      const completedGoals = goals.filter(g => g.status === 'active' && g.current_amount >= g.target_amount);
      const inProgressGoals = goals.filter(g => g.status === 'active' && g.current_amount < g.target_amount);

      if (completedGoals.length > 0 || inProgressGoals.length > 0) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.text("Goals Status", 14, yPos);
        yPos += 8;

        if (completedGoals.length > 0) {
          doc.setFontSize(12);
          doc.text("Completed Goals:", 14, yPos);
          yPos += 6;
          const completedData = completedGoals.map(g => [
            g.title,
            `£${g.target_amount.toFixed(2)}`,
            `£${g.current_amount.toFixed(2)}`
          ]);
          autoTable(doc, {
            startY: yPos,
            head: [['Goal', 'Target', 'Raised']],
            body: completedData,
            theme: 'striped',
            headStyles: { fillColor: [34, 197, 94] },
          });
          yPos = (doc as any).lastAutoTable.finalY + 10;
        }

        if (inProgressGoals.length > 0) {
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }
          doc.setFontSize(12);
          doc.text("Goals in Progress:", 14, yPos);
          yPos += 6;
          const progressData = inProgressGoals.map(g => [
            g.title,
            `£${g.target_amount.toFixed(2)}`,
            `£${g.current_amount.toFixed(2)}`,
            `${((g.current_amount / g.target_amount) * 100).toFixed(1)}%`
          ]);
          autoTable(doc, {
            startY: yPos,
            head: [['Goal', 'Target', 'Raised', 'Progress']],
            body: progressData,
            theme: 'striped',
            headStyles: { fillColor: [251, 191, 36] },
          });
          yPos = (doc as any).lastAutoTable.finalY + 15;
        }
      }

      // Ring Fences
      if (ringFences.filter(rf => rf.status === 'active').length > 0) {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(14);
        doc.text("Ring Fenced Funds", 14, yPos);
        yPos += 8;

        const ringFenceData = ringFences
          .filter(rf => rf.status === 'active')
          .map(rf => [rf.title, `£${rf.amount.toFixed(2)}`, rf.details || '-']);

        autoTable(doc, {
          startY: yPos,
          head: [['Title', 'Amount', 'Details']],
          body: ringFenceData,
          theme: 'striped',
          headStyles: { fillColor: [147, 51, 234] },
        });
      }

      doc.save(`AGM-Treasurers-Report-${new Date().getFullYear()}.pdf`);
      
      toast({
        title: "Report Generated",
        description: "AGM Treasurer's Report has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate AGM report.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const calculateFloatTotal = (float: CashFloat) => {
    return (
      float.fifty_pound * 50 +
      float.twenty_pound * 20 +
      float.ten_pound * 10 +
      float.five_pound * 5 +
      float.two_pound * 2 +
      float.one_pound * 1 +
      float.fifty_pence * 0.50 +
      float.twenty_pence * 0.20 +
      float.ten_pence * 0.10 +
      float.five_pence * 0.05 +
      float.two_pence * 0.02 +
      float.one_pence * 0.01
    );
  };

  const renderDenomination = (value: number, count: number, isNote: boolean) => {
    if (count === 0) return null;
    return (
      <div className="flex items-center gap-2 text-sm">
        {isNote ? <Banknote className="h-4 w-4 text-green-600" /> : <Coins className="h-4 w-4 text-amber-600" />}
        <span>£{value.toFixed(value < 1 ? 2 : 0)} × {count}</span>
      </div>
    );
  };

  // Format financial year with proper year calculation
  const formatFinancialYear = (year: FinancialYear) => {
    const start = new Date(year.start_date);
    const end = new Date(year.end_date);
    
    const startFormatted = start.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    const endFormatted = end.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const getTransactionsForYear = (year: FinancialYear) => {
    const startDate = new Date(year.start_date);
    const endDate = new Date(year.end_date);
    return transactions.filter(t => {
      const tDate = new Date(t.transaction_date);
      return tDate >= startDate && tDate <= endDate;
    });
  };

  const downloadFinancialYearPDF = async (year: FinancialYear) => {
    setIsGeneratingPDF(true);
    try {
      const doc = new jsPDF();
      const yearTransactions = getTransactionsForYear(year);
      const yearRange = formatFinancialYear(year);
      
      // Title
      doc.setFontSize(18);
      doc.text(`Financial Year Report`, 14, 20);
      doc.setFontSize(12);
      doc.text(yearRange, 14, 28);
      
      // Summary
      const totalIncome = yearTransactions
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + t.amount, 0);
      const totalExpenses = yearTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + t.amount, 0);
      const netBalance = totalIncome - totalExpenses;
      
      doc.setFontSize(10);
      doc.text(`Total Income: £${totalIncome.toFixed(2)}`, 14, 38);
      doc.text(`Total Expenses: £${totalExpenses.toFixed(2)}`, 14, 44);
      doc.text(`Net Balance: £${netBalance.toFixed(2)}`, 14, 50);
      
      // Transactions table
      autoTable(doc, {
        startY: 58,
        head: [['Date', 'Type', 'Category', 'Description', 'Amount']],
        body: yearTransactions.map(t => [
          new Date(t.transaction_date).toLocaleDateString(),
          t.type,
          t.category,
          t.description || '',
          `£${t.amount.toFixed(2)}`
        ]),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
      });
      
      doc.save(`${yearRange} finances.pdf`);
      
      toast({
        title: "PDF Downloaded",
        description: `Financial report for ${yearRange} has been downloaded.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate PDF report.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const personalTasks = tasks.filter(t => t.task_type === 'personal' || t.task_type === 'assigned');
  const needsNewYear = currentFinancialYear && new Date() > new Date(currentFinancialYear.end_date);
  
  // Get archived years
  const archivedYears = financialYears.filter(y => y.is_archived);

  return (
    <div className="flex-1 bg-gradient-to-br from-background to-muted/20 p-4 md:p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex items-center justify-between">
            <div>
        <h1 className="text-4xl font-bold text-foreground">
          Treasurer Dashboard
        </h1>
              <p className="text-muted-foreground mt-2">Manage finances and track expenses</p>
            </div>
          </div>

          {needsNewYear && (
            <Card className="border-destructive bg-destructive/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertCircle className="h-5 w-5" />
                  <p className="font-medium">
                    The current financial year has ended. Please start a new financial year in Settings.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="inline-flex h-auto items-center justify-start gap-8 border-b border-border w-full flex-wrap">
              <TabsTrigger value="overview" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Overview</TabsTrigger>
              <TabsTrigger value="transactions" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Transactions</TabsTrigger>
              <TabsTrigger value="expenses" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Expense Claims</TabsTrigger>
              <TabsTrigger value="floats" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Cash Floats</TabsTrigger>
              <TabsTrigger value="reports" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Reports</TabsTrigger>
              <TabsTrigger value="bank" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Bank Access</TabsTrigger>
              <TabsTrigger value="alerts" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Alerts</TabsTrigger>
              <TabsTrigger value="tasks" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Tasks</TabsTrigger>
              <TabsTrigger value="ringfences" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Ring Fences</TabsTrigger>
              <TabsTrigger value="settings" className="inline-flex items-center justify-center whitespace-nowrap pb-3 px-0 text-sm font-medium text-muted-foreground transition-all border-b-2 border-transparent data-[state=active]:text-primary data-[state=active]:border-primary hover:text-foreground">Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Income</CardTitle>
                    <DollarSign className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      £{transactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
                    <DollarSign className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">
                      £{transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Pending Claims</CardTitle>
                    <FileText className="h-4 w-4 text-yellow-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {expenseClaims.filter(c => c.status === 'pending').length}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Cash Floats</CardTitle>
                    <Wallet className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{cashFloats.length}</div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="transactions" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Transactions</CardTitle>
                      <CardDescription>Track income and expenses</CardDescription>
                    </div>
                    <Dialog open={isTransactionDialogOpen} onOpenChange={setIsTransactionDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Transaction
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Transaction</DialogTitle>
                          <DialogDescription>Record a new income or expense</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAddTransaction} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="type">Type</Label>
                            <Select name="type" required>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="income">Income</SelectItem>
                                <SelectItem value="expense">Expense</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="amount">Amount (£)</Label>
                            <Input id="amount" name="amount" type="number" step="0.01" placeholder="0.00" required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="category">Category</Label>
                            <Select value={selectedCategory} onValueChange={setSelectedCategory} required>
                              <SelectTrigger>
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {selectedCategory === "Other" && (
                            <div className="space-y-2">
                              <Label htmlFor="custom_category">Custom Category</Label>
                              <Input 
                                id="custom_category" 
                                value={customCategory}
                                onChange={(e) => setCustomCategory(e.target.value)}
                                placeholder="Enter category name" 
                                required 
                              />
                            </div>
                          )}
                          {selectedCategory === "Event" && (
                            <div className="space-y-2">
                              <Label htmlFor="event_id">Event *</Label>
                              <Select name="event_id" required>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select event" />
                                </SelectTrigger>
                                <SelectContent>
                                  {events.map(event => (
                                    <SelectItem key={event.id} value={event.id}>
                                      {event.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea id="description" name="description" placeholder="Optional notes" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="date">Date</Label>
                            <Input id="date" name="date" type="date" required />
                          </div>
                          <div className="flex justify-end gap-2">
                            <DialogClose asChild>
                              <Button type="button" variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button type="submit">Add Transaction</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Search transactions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-sm"
                    />
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Filter type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="income">Income</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions
                        .filter(t => {
                          const matchesSearch = t.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                              t.category.toLowerCase().includes(searchQuery.toLowerCase());
                          const matchesType = filterType === 'all' || t.type === filterType;
                          return matchesSearch && matchesType;
                        })
                        .map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell>{new Date(transaction.transaction_date).toLocaleDateString()}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                transaction.type === 'income' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {transaction.type}
                              </span>
                            </TableCell>
                            <TableCell>{transaction.category}</TableCell>
                            <TableCell>{transaction.description}</TableCell>
                            <TableCell className={transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                              £{transaction.amount.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingTransaction(transaction);
                                          setEditTransactionCategory(transaction.category);
                                          setIsEditTransactionDialogOpen(true);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit transaction</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setDeletingTransactionId(transaction.id);
                                          setIsDeleteTransactionDialogOpen(true);
                                        }}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete transaction</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>

                  {/* Edit Transaction Dialog */}
                  <Dialog open={isEditTransactionDialogOpen} onOpenChange={setIsEditTransactionDialogOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Edit Transaction</DialogTitle>
                        <DialogDescription>Update transaction details</DialogDescription>
                      </DialogHeader>
                      {editingTransaction && (
                        <form onSubmit={handleEditTransaction} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Type</Label>
                            <Select name="type" defaultValue={editingTransaction.type} required>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="income">Income</SelectItem>
                                <SelectItem value="expense">Expense</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Amount (£)</Label>
                            <Input name="amount" type="number" step="0.01" defaultValue={editingTransaction.amount} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Category</Label>
                            <Select value={editTransactionCategory} onValueChange={setEditTransactionCategory} required>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {categories.map(cat => (
                                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {editTransactionCategory === "Other" && (
                            <div className="space-y-2">
                              <Label>Custom Category</Label>
                              <Input name="custom_category" placeholder="Enter category name" required />
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea name="description" defaultValue={editingTransaction.description || ''} />
                          </div>
                          <div className="space-y-2">
                            <Label>Date</Label>
                            <Input name="date" type="date" defaultValue={editingTransaction.transaction_date.split('T')[0]} required />
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setIsEditTransactionDialogOpen(false)}>Cancel</Button>
                            <Button type="submit">Update Transaction</Button>
                          </div>
                        </form>
                      )}
                    </DialogContent>
                  </Dialog>

                  {/* Delete Transaction Dialog */}
                  <Dialog open={isDeleteTransactionDialogOpen} onOpenChange={setIsDeleteTransactionDialogOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Delete Transaction</DialogTitle>
                        <DialogDescription>Please provide a reason for deleting this transaction.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Reason for deletion *</Label>
                          <Textarea 
                            value={deleteTransactionReason}
                            onChange={(e) => setDeleteTransactionReason(e.target.value)}
                            placeholder="Enter the reason for deleting this transaction..."
                            required
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => {
                            setIsDeleteTransactionDialogOpen(false);
                            setDeletingTransactionId(null);
                            setDeleteTransactionReason("");
                          }}>Cancel</Button>
                          <Button 
                            variant="destructive" 
                            onClick={handleDeleteTransaction}
                            disabled={!deleteTransactionReason.trim()}
                          >
                            Delete Transaction
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Expense Claims</CardTitle>
                      <CardDescription>Manage reimbursement requests</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Dialog open={isExpenseClaimDialogOpen} onOpenChange={setIsExpenseClaimDialogOpen}>
                        <DialogTrigger asChild>
                          <Button>
                            <Plus className="h-4 w-4 mr-2" />
                            Submit Claim
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Submit Expense Claim</DialogTitle>
                            <DialogDescription>Request reimbursement for an expense</DialogDescription>
                          </DialogHeader>
                          <form onSubmit={handleAddExpenseClaim} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="claimant_name">Claimant Name</Label>
                              <Select name="claimant_name" required>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {members.map(member => (
                                    <SelectItem key={member.id} value={member.full_name}>
                                      {member.full_name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="amount">Amount (£)</Label>
                              <Input id="amount" name="amount" type="number" step="0.01" placeholder="0.00" required />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="description">Description</Label>
                              <Textarea 
                                id="description" 
                                name="description" 
                                placeholder="What was this expense for?" 
                                required 
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="event_id">Event (Optional)</Label>
                              <Select name="event_id">
                                <SelectTrigger>
                                  <SelectValue placeholder="Select event" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None</SelectItem>
                                  {events.map(event => (
                                    <SelectItem key={event.id} value={event.id}>
                                      {event.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Receipt (Optional)</Label>
                              <div 
                                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                  isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary'
                                }`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('receipt-upload')?.click()}
                              >
                                <input
                                  id="receipt-upload"
                                  type="file"
                                  accept="image/*,.pdf"
                                  className="hidden"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) setReceiptFile(file);
                                  }}
                                />
                                {receiptFile ? (
                                  <div className="flex items-center justify-center gap-2">
                                    <FileText className="h-5 w-5 text-primary" />
                                    <span className="text-sm font-medium">{receiptFile.name}</span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setReceiptFile(null);
                                      }}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                                    <p className="text-sm text-muted-foreground">
                                      Drag and drop a receipt or click to upload
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      Supports images and PDFs
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex justify-end gap-2">
                              <DialogClose asChild>
                                <Button type="button" variant="outline">Cancel</Button>
                              </DialogClose>
                              <Button type="submit">Submit Claim</Button>
                            </div>
                          </form>
                        </DialogContent>
                      </Dialog>
                      
                      {/* Edit Expense Claim Dialog */}
                      <Dialog open={isEditExpenseClaimDialogOpen} onOpenChange={setIsEditExpenseClaimDialogOpen}>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Edit Expense Claim</DialogTitle>
                            <DialogDescription>Update expense claim details</DialogDescription>
                          </DialogHeader>
                          {editingExpenseClaim && (
                            <form onSubmit={handleEditExpenseClaim} className="space-y-4">
                              <div className="space-y-2">
                                <Label>Claimant Name</Label>
                                <Input value={editingExpenseClaim.claimant_name} disabled />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="edit-amount">Amount (£)</Label>
                                <Input 
                                  id="edit-amount" 
                                  name="amount" 
                                  type="number" 
                                  step="0.01" 
                                  defaultValue={editingExpenseClaim.amount}
                                  required 
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="edit-description">Description</Label>
                                <Textarea 
                                  id="edit-description" 
                                  name="description" 
                                  defaultValue={editingExpenseClaim.description}
                                  required 
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="edit-event_id">Event (Optional)</Label>
                                <Select name="event_id" defaultValue={editingExpenseClaim.event_id || 'none'}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select event" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {events.map(event => (
                                      <SelectItem key={event.id} value={event.id}>
                                        {event.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex justify-end gap-2">
                                <DialogClose asChild>
                                  <Button type="button" variant="outline">Cancel</Button>
                                </DialogClose>
                                <Button type="submit">Update Claim</Button>
                              </div>
                            </form>
                          )}
                        </DialogContent>
                      </Dialog>
                      
                      <Dialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline">
                            <Archive className="h-4 w-4 mr-2" />
                            View Archived
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl">
                          <DialogHeader>
                            <DialogTitle>Archived Claims</DialogTitle>
                            <DialogDescription>View past expense claims</DialogDescription>
                          </DialogHeader>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Claimant</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Archived Date</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {archivedClaims.map((claim) => (
                                <TableRow key={claim.id}>
                                  <TableCell>{claim.claimant_name}</TableCell>
                                  <TableCell>{claim.description}</TableCell>
                                  <TableCell>£{claim.amount.toFixed(2)}</TableCell>
                                  <TableCell>
                                    {claim.archived_at ? new Date(claim.archived_at).toLocaleDateString() : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Claimant</TableHead>
                        <TableHead>Date Submitted</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenseClaims.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No expense claims found
                          </TableCell>
                        </TableRow>
                      ) : (
                        expenseClaims.map((claim) => (
                          <TableRow key={claim.id}>
                            <TableCell className="font-medium">{claim.claimant_name}</TableCell>
                            <TableCell>{new Date(claim.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                            <TableCell>{claim.description}</TableCell>
                            <TableCell>£{claim.amount.toFixed(2)}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                claim.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                                claim.status === 'approved' ? 'bg-green-100 text-green-700' :
                                claim.status === 'waiting_approval' ? 'bg-orange-100 text-orange-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {claim.status === 'paid' ? 'Paid' : 
                                 claim.status === 'approved' ? 'Approved' :
                                 claim.status === 'waiting_approval' ? 'Awaiting Trustee' :
                                 claim.status === 'pending' ? 'Pending' : claim.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {claim.receipt_url && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => window.open(claim.receipt_url || '', '_blank')}
                                        >
                                          <FileText className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>View receipt</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingExpenseClaim(claim);
                                          setIsEditExpenseClaimDialogOpen(true);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit claim</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteExpenseClaim(claim.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete claim</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                {claim.status === 'pending' && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleApproveClaim(claim.id)}
                                          className={trusteePolicies?.enabled && claim.amount > trusteePolicies.approval_threshold 
                                            ? "text-orange-500 hover:text-orange-600" 
                                            : ""}
                                        >
                                          {trusteePolicies?.enabled && claim.amount > trusteePolicies.approval_threshold 
                                            ? <Clock className="h-4 w-4" />
                                            : <CheckCircle className="h-4 w-4" />}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        {trusteePolicies?.enabled && claim.amount > trusteePolicies.approval_threshold 
                                          ? `Send for trustee approval (exceeds £${trusteePolicies.approval_threshold} threshold)`
                                          : "Approve claim"}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {claim.status === 'approved' && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleMarkPaid(claim.id)}
                                        >
                                          <DollarSign className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Mark as paid</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                                {claim.status === 'paid' && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleArchiveClaim(claim.id)}
                                        >
                                          <Archive className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Archive claim</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="allocations" className="space-y-3">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Fund Allocations</CardTitle>
                      <CardDescription>Ring-fence funds for specific goals</CardDescription>
                    </div>
                    <Dialog open={isAllocationDialogOpen} onOpenChange={setIsAllocationDialogOpen}>
                      <DialogTrigger asChild>
                        <Button><Plus className="h-4 w-4 mr-2" />Add Allocation</Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Fund Allocation</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddAllocation} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Link to Goal (Optional)</Label>
                            <Select name="goal_id" defaultValue="none">
                              <SelectTrigger><SelectValue placeholder="Select goal" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {goals.map(g => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Goal Name</Label>
                            <Input name="goal_name" required />
                          </div>
                          <div className="space-y-2">
                            <Label>Amount</Label>
                            <Input name="amount" type="number" step="0.01" required />
                          </div>
                          <div className="space-y-2">
                            <Label>Estimated Spend Date</Label>
                            <Input name="estimated_spend_date" type="date" required />
                          </div>
                          <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea name="description" />
                          </div>
                          <Button type="submit">Add Allocation</Button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Goal Name</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Spend Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allocations.map(a => (
                        <TableRow key={a.id}>
                          <TableCell>{a.goal_name}</TableCell>
                          <TableCell>£{a.amount.toFixed(2)}</TableCell>
                          <TableCell>{new Date(a.estimated_spend_date).toLocaleDateString()}</TableCell>
                          <TableCell>{a.description}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAllocation(a.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="floats" className="space-y-3">
              <div className="flex justify-end mb-4">
                <Dialog open={isFloatDialogOpen} onOpenChange={setIsFloatDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Float
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Cash Float</DialogTitle>
                      <DialogDescription>Set up a new cash float with denomination breakdown</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateFloat} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="float_name">Float Name</Label>
                        <Input id="float_name" name="float_name" placeholder="e.g., Summer Fair Float" required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="float_location">Location</Label>
                        <Input id="float_location" name="float_location" placeholder="e.g., Main Office" />
                      </div>
                      <div className="space-y-3">
                        <Label>Denominations</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="fifty_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £50 Notes
                            </Label>
                            <Input id="fifty_pound" name="fifty_pound" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="twenty_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £20 Notes
                            </Label>
                            <Input id="twenty_pound" name="twenty_pound" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="ten_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £10 Notes
                            </Label>
                            <Input id="ten_pound" name="ten_pound" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="five_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £5 Notes
                            </Label>
                            <Input id="five_pound" name="five_pound" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="two_pound" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              £2 Coins
                            </Label>
                            <Input id="two_pound" name="two_pound" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="one_pound" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              £1 Coins
                            </Label>
                            <Input id="one_pound" name="one_pound" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="fifty_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              50p Coins
                            </Label>
                            <Input id="fifty_pence" name="fifty_pence" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="twenty_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              20p Coins
                            </Label>
                            <Input id="twenty_pence" name="twenty_pence" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="ten_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              10p Coins
                            </Label>
                            <Input id="ten_pence" name="ten_pence" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="five_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              5p Coins
                            </Label>
                            <Input id="five_pence" name="five_pence" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="two_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              2p Coins
                            </Label>
                            <Input id="two_pence" name="two_pence" type="number" min="0" defaultValue="0" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="one_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              1p Coins
                            </Label>
                            <Input id="one_pence" name="one_pence" type="number" min="0" defaultValue="0" />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button type="submit">Create Float</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card className="shadow-sm">
                <CardContent className="pt-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Denominations</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Last Counted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cashFloats.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            No cash floats found
                          </TableCell>
                        </TableRow>
                      ) : (
                        cashFloats.map((float) => {
                          const latestCount = floatHistory
                            .filter(h => h.float_id === float.id)
                            .sort((a, b) => new Date(b.counted_at).getTime() - new Date(a.counted_at).getTime())[0];
                          
                          const denominations = [
                            { value: 50, count: float.fifty_pound, isNote: true },
                            { value: 20, count: float.twenty_pound, isNote: true },
                            { value: 10, count: float.ten_pound, isNote: true },
                            { value: 5, count: float.five_pound, isNote: true },
                            { value: 2, count: float.two_pound, isNote: false },
                            { value: 1, count: float.one_pound, isNote: false },
                            { value: 0.50, count: float.fifty_pence, isNote: false },
                            { value: 0.20, count: float.twenty_pence, isNote: false },
                            { value: 0.10, count: float.ten_pence, isNote: false },
                            { value: 0.05, count: float.five_pence, isNote: false },
                            { value: 0.02, count: float.two_pence, isNote: false },
                            { value: 0.01, count: float.one_pence, isNote: false },
                          ].filter(d => d.count > 0);

                          return (
                            <TableRow key={float.id}>
                              <TableCell className="font-medium">{float.float_name}</TableCell>
                              <TableCell>{float.float_location || '-'}</TableCell>
                              <TableCell className="font-bold">£{calculateFloatTotal(float).toFixed(2)}</TableCell>
                              <TableCell>
                                {denominations.length === 0 ? (
                                  <span className="text-muted-foreground text-sm">Empty</span>
                                ) : (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-sm cursor-help underline decoration-dotted">
                                          {denominations.length} {denominations.length === 1 ? 'type' : 'types'}
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent className="max-w-xs">
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 p-1">
                                          {denominations.map((d, idx) => (
                                            <div key={idx} className="flex items-center gap-1.5 text-xs">
                                              {d.isNote ? (
                                                <Banknote className="h-3 w-3 text-green-500" />
                                              ) : (
                                                <Coins className="h-3 w-3 text-amber-500" />
                                              )}
                                              <span>£{d.value.toFixed(d.value < 1 ? 2 : 0)} × {d.count}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant={float.status === 'available' ? 'default' : float.status === 'assigned' ? 'secondary' : 'outline'}>
                                  {float.status}
                                </Badge>
                                {float.needs_count && (
                                  <Badge variant="destructive" className="ml-1">Count needed</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                {latestCount ? (
                                  <span className="text-sm">{new Date(latestCount.counted_at).toLocaleDateString()}</span>
                                ) : (
                                  <span className="text-muted-foreground text-sm">Never</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => openCountDialog(float)}>
                                          <Coins className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Count float</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => openHistoryDialog(float)}>
                                          <History className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>View count history</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => {
                                          setEditingFloat(float);
                                          setShowEditFloatDialog(true);
                                        }}>
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Edit float</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteFloat(float.id)}>
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Delete float</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Edit Float Dialog */}
              <Dialog open={showEditFloatDialog} onOpenChange={setShowEditFloatDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Edit Cash Float</DialogTitle>
                    <DialogDescription>Update float details and denominations</DialogDescription>
                  </DialogHeader>
                  {editingFloat && (
                    <form onSubmit={handleUpdateFloat} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit_float_name">Float Name</Label>
                        <Input 
                          id="edit_float_name" 
                          name="float_name" 
                          defaultValue={editingFloat.float_name}
                          required 
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit_float_location">Location</Label>
                        <Input 
                          id="edit_float_location" 
                          name="float_location" 
                          defaultValue={editingFloat.float_location || ''}
                        />
                      </div>
                      <div className="space-y-3">
                        <Label>Denominations</Label>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="edit_fifty_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £50 Notes
                            </Label>
                            <Input 
                              id="edit_fifty_pound" 
                              name="fifty_pound" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.fifty_pound}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_twenty_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £20 Notes
                            </Label>
                            <Input 
                              id="edit_twenty_pound" 
                              name="twenty_pound" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.twenty_pound}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_ten_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £10 Notes
                            </Label>
                            <Input 
                              id="edit_ten_pound" 
                              name="ten_pound" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.ten_pound}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_five_pound" className="flex items-center gap-2">
                              <Banknote className="h-4 w-4 text-green-600" />
                              £5 Notes
                            </Label>
                            <Input 
                              id="edit_five_pound" 
                              name="five_pound" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.five_pound}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_two_pound" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              £2 Coins
                            </Label>
                            <Input 
                              id="edit_two_pound" 
                              name="two_pound" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.two_pound}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_one_pound" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              £1 Coins
                            </Label>
                            <Input 
                              id="edit_one_pound" 
                              name="one_pound" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.one_pound}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_fifty_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              50p Coins
                            </Label>
                            <Input 
                              id="edit_fifty_pence" 
                              name="fifty_pence" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.fifty_pence}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_twenty_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              20p Coins
                            </Label>
                            <Input 
                              id="edit_twenty_pence" 
                              name="twenty_pence" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.twenty_pence}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_ten_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              10p Coins
                            </Label>
                            <Input 
                              id="edit_ten_pence" 
                              name="ten_pence" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.ten_pence}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_five_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              5p Coins
                            </Label>
                            <Input 
                              id="edit_five_pence" 
                              name="five_pence" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.five_pence}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_two_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              2p Coins
                            </Label>
                            <Input 
                              id="edit_two_pence" 
                              name="two_pence" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.two_pence}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit_one_pence" className="flex items-center gap-2">
                              <Coins className="h-4 w-4 text-amber-600" />
                              1p Coins
                            </Label>
                            <Input 
                              id="edit_one_pence" 
                              name="one_pence" 
                              type="number" 
                              min="0" 
                              defaultValue={editingFloat.one_pence}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => {
                          setShowEditFloatDialog(false);
                          setEditingFloat(null);
                        }}>
                          Cancel
                        </Button>
                        <Button type="submit">Update Float</Button>
                      </div>
                    </form>
                  )}
                </DialogContent>
              </Dialog>

              {/* Count Dialog */}
              <Dialog open={countDialogOpen} onOpenChange={setCountDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Cash Count</DialogTitle>
                    <DialogDescription>
                      {selectedFloat?.float_name} - Record count details
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="count_date">Count Date *</Label>
                      <Input
                        id="count_date"
                        type="date"
                        value={countDate}
                        onChange={(e) => setCountDate(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Counters *</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {members.map((member) => (
                          <div key={member.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`counter-${member.id}`}
                              checked={selectedCounters.includes(member.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCounters([...selectedCounters, member.id]);
                                } else {
                                  setSelectedCounters(selectedCounters.filter(id => id !== member.id));
                                }
                              }}
                            />
                            <Label htmlFor={`counter-${member.id}`} className="cursor-pointer">
                              {member.full_name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                    {trusteePolicies?.require_two_counters && (
                      <p className="text-sm text-yellow-600 font-medium">
                        ⚠ Policy requires at least 2 counters
                      </p>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="count_notes">Notes (Optional)</Label>
                      <Textarea
                        id="count_notes"
                        value={countNotes}
                        onChange={(e) => setCountNotes(e.target.value)}
                        placeholder="Any observations or discrepancies..."
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setCountDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveCount}>
                        Save Count
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* History Dialog */}
              <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Count History - {selectedFloat?.float_name}</DialogTitle>
                    <DialogDescription>
                      View all previous counts for this float
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    {floatHistory.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">
                        No count history available
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {floatHistory.map((history) => (
                          <Card key={history.id} className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-semibold">
                                  {new Date(history.counted_at).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Counted by: {history.counted_by.map(id => 
                                    members.find(m => m.id === id)?.full_name || 'Unknown'
                                  ).join(', ')}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold">£{history.total_amount.toFixed(2)}</p>
                              </div>
                            </div>
                            {history.notes && (
                              <p className="text-sm text-muted-foreground mb-2">
                                <strong>Notes:</strong> {history.notes}
                              </p>
                            )}
                            {history.denominations && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground pt-2 border-t">
                                {history.denominations.fifty_pound > 0 && <div>£50: {history.denominations.fifty_pound}</div>}
                                {history.denominations.twenty_pound > 0 && <div>£20: {history.denominations.twenty_pound}</div>}
                                {history.denominations.ten_pound > 0 && <div>£10: {history.denominations.ten_pound}</div>}
                                {history.denominations.five_pound > 0 && <div>£5: {history.denominations.five_pound}</div>}
                                {history.denominations.two_pound > 0 && <div>£2: {history.denominations.two_pound}</div>}
                                {history.denominations.one_pound > 0 && <div>£1: {history.denominations.one_pound}</div>}
                                {history.denominations.fifty_pence > 0 && <div>50p: {history.denominations.fifty_pence}</div>}
                                {history.denominations.twenty_pence > 0 && <div>20p: {history.denominations.twenty_pence}</div>}
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="reports" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Financial Reports</CardTitle>
                  <CardDescription>Generate and download reports</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => generatePDF('monthly')}>
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-semibold">Monthly Report</h3>
                          <p className="text-sm text-muted-foreground">Last 30 days</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => generatePDF('yearly')}>
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-semibold">Yearly Report</h3>
                          <p className="text-sm text-muted-foreground">Last 12 months</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => setShowCustomReportDialog(true)}>
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-semibold">Custom Report</h3>
                          <p className="text-sm text-muted-foreground">Select date range</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => generateAGMReport()}>
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-semibold">Treasurer's Report For AGM</h3>
                          <p className="text-sm text-muted-foreground">Comprehensive annual report</p>
                        </div>
                      </div>
                    </Card>

                    <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={handleExportCSV}>
                      <div className="flex items-center gap-3">
                        <Download className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-semibold">Export CSV</h3>
                          <p className="text-sm text-muted-foreground">All transactions</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Custom Report Dialog */}
              <Dialog open={showCustomReportDialog} onOpenChange={setShowCustomReportDialog}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Custom Report</DialogTitle>
                    <DialogDescription>Select a date range for your custom report</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="custom_start">Start Date</Label>
                      <Input
                        id="custom_start"
                        type="date"
                        value={customReportStart}
                        onChange={(e) => setCustomReportStart(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom_end">End Date</Label>
                      <Input
                        id="custom_end"
                        type="date"
                        value={customReportEnd}
                        onChange={(e) => setCustomReportEnd(e.target.value)}
                        required
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setShowCustomReportDialog(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (customReportStart && customReportEnd) {
                            generatePDF('custom', customReportStart, customReportEnd);
                            setShowCustomReportDialog(false);
                            setCustomReportStart("");
                            setCustomReportEnd("");
                          } else {
                            toast({
                              title: "Error",
                              description: "Please select both start and end dates",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        Generate Report
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="bank" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Bank Access Log</CardTitle>
                      <CardDescription>Track who has access to bank accounts</CardDescription>
                    </div>
                    <Dialog open={isBankAccessDialogOpen} onOpenChange={setIsBankAccessDialogOpen}>
                      <DialogTrigger asChild>
                        <Button onClick={() => setEditingBankAccess(null)}>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Access
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{editingBankAccess ? 'Edit' : 'Add'} Bank Access</DialogTitle>
                          <DialogDescription>
                            Record bank access permissions for a member
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={editingBankAccess ? handleUpdateBankAccess : handleAddBankAccess} className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="member_id">Member</Label>
                            <Select name="member_id" defaultValue={editingBankAccess?.member_id} required>
                              <SelectTrigger>
                                <SelectValue placeholder="Select member" />
                              </SelectTrigger>
                              <SelectContent>
                                {members.map(member => (
                                  <SelectItem key={member.id} value={member.id}>
                                    {member.full_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-3">
                            <Label>Access Permissions</Label>
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="is_signatory" 
                                  name="is_signatory"
                                  defaultChecked={editingBankAccess?.is_signatory}
                                />
                                <Label htmlFor="is_signatory" className="cursor-pointer">Bank Signatory</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="has_online_access" 
                                  name="has_online_access"
                                  defaultChecked={editingBankAccess?.has_online_access}
                                />
                                <Label htmlFor="has_online_access" className="cursor-pointer">Online Banking Access</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="has_debit_card" 
                                  name="has_debit_card"
                                  defaultChecked={editingBankAccess?.has_debit_card}
                                />
                                <Label htmlFor="has_debit_card" className="cursor-pointer">Debit Card</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="has_credit_card" 
                                  name="has_credit_card"
                                  defaultChecked={editingBankAccess?.has_credit_card}
                                />
                                <Label htmlFor="has_credit_card" className="cursor-pointer">Credit Card</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="has_cheque_book" 
                                  name="has_cheque_book"
                                  defaultChecked={editingBankAccess?.has_cheque_book}
                                />
                                <Label htmlFor="has_cheque_book" className="cursor-pointer">Cheque Book</Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox 
                                  id="has_paying_in_book" 
                                  name="has_paying_in_book"
                                  defaultChecked={editingBankAccess?.has_paying_in_book}
                                />
                                <Label htmlFor="has_paying_in_book" className="cursor-pointer">Paying-in Book</Label>
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end gap-2">
                            <DialogClose asChild>
                              <Button type="button" variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button type="submit">{editingBankAccess ? 'Update' : 'Add'} Access</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Signatory</TableHead>
                        <TableHead>Online Access</TableHead>
                        <TableHead>Debit Card</TableHead>
                        <TableHead>Credit Card</TableHead>
                        <TableHead>Cheque Book</TableHead>
                        <TableHead>Paying-in Book</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bankAccessLog.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground">
                            No bank access records found
                          </TableCell>
                        </TableRow>
                      ) : (
                        bankAccessLog.map((access) => (
                          <TableRow key={access.id}>
                            <TableCell className="font-medium">
                              {access.member?.full_name || 'Unknown'}
                            </TableCell>
                            <TableCell>
                              {access.is_signatory ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              {access.has_online_access ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              {access.has_debit_card ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              {access.has_credit_card ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              {access.has_cheque_book ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              {access.has_paying_in_book ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <X className="h-4 w-4 text-muted-foreground" />
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setEditingBankAccess(access);
                                          setIsBankAccessDialogOpen(true);
                                        }}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit access</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteBankAccess(access.id)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete access</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="alerts" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Alerts</CardTitle>
                      <CardDescription>Manage renewal reminders and custom alerts</CardDescription>
                    </div>
                    <Dialog open={isAlertDialogOpen} onOpenChange={setIsAlertDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Alert
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Alert</DialogTitle>
                          <DialogDescription>Create a new reminder or alert</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleAddAlert} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Quick Add (Optional)</Label>
                            <div className="grid grid-cols-1 gap-2">
                              {presetAlerts.map(preset => (
                                <Button
                                  key={preset.title}
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const titleInput = document.getElementById('alert_title') as HTMLInputElement;
                                    if (titleInput) titleInput.value = preset.title;
                                  }}
                                >
                                  {preset.title}
                                </Button>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="alert_title">Alert Title</Label>
                            <Input id="alert_title" name="title" placeholder="e.g., Insurance Renewal" required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="due_date">Due Date</Label>
                            <Input id="due_date" name="due_date" type="date" required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="alert_date">Alert Date (When to be notified)</Label>
                            <Input id="alert_date" name="alert_date" type="date" required />
                          </div>
                          <div className="flex justify-end gap-2">
                            <DialogClose asChild>
                              <Button type="button" variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button type="submit">Add Alert</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Alert Date</TableHead>
                        <TableHead>Days Until Due</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alerts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground">
                            No alerts found
                          </TableCell>
                        </TableRow>
                      ) : (
                        alerts.map((alert) => {
                          const daysUntilDue = Math.ceil((new Date(alert.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                          const isCritical = daysUntilDue <= 7 && daysUntilDue >= 0;
                          
                          return (
                            <TableRow key={alert.id} className={isCritical ? 'bg-destructive/5' : ''}>
                              <TableCell className="font-medium">{alert.title}</TableCell>
                              <TableCell>{new Date(alert.due_date).toLocaleDateString()}</TableCell>
                              <TableCell>{new Date(alert.alert_date).toLocaleDateString()}</TableCell>
                              <TableCell>
                                <span className={isCritical ? 'text-destructive font-medium' : ''}>
                                  {daysUntilDue > 0 ? `${daysUntilDue} days` : 'Overdue'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setEditingAlert(alert);
                                            setIsEditAlertDialogOpen(true);
                                          }}
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Edit alert</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDeleteAlert(alert.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Delete alert</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tasks" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>My Tasks</CardTitle>
                  <CardDescription>Personal tasks and assignments</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddTask} className="flex gap-2 mb-4">
                    <Input
                      placeholder="New task title..."
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Description (optional)..."
                      value={newTaskDescription}
                      onChange={(e) => setNewTaskDescription(e.target.value)}
                      className="flex-1"
                    />
                    <Button type="submit">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Task
                    </Button>
                  </form>

                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">Personal & Assigned Tasks</h3>
                      <div className="space-y-2">
                        {personalTasks.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No tasks yet</p>
                        ) : (
                          personalTasks.map((task) => (
                            <div key={task.id} className="flex items-center gap-2 p-3 border rounded-lg">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleToggleTask(task.id, task.status)}
                                    >
                                      {task.status === 'completed' ? (
                                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                                      ) : (
                                        <Circle className="h-5 w-5" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>{task.status === 'completed' ? 'Mark as pending' : 'Mark as complete'}</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <div className="flex-1">
                                <p className={`font-medium ${task.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                                  {task.title}
                                </p>
                                {task.description && (
                                  <p className="text-sm text-muted-foreground">{task.description}</p>
                                )}
                                {task.task_type === 'assigned' && (
                                  <p className="text-xs text-muted-foreground mt-1">Assigned by chair/secretary</p>
                                )}
                              </div>
                              {task.task_type === 'personal' && task.created_by === currentUserId && (
                                <>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setEditingTask(task);
                                            setIsEditTaskDialogOpen(true);
                                          }}
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Edit task</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDeleteTask(task.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Delete task</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {personalTasks.some(t => t.status === 'completed') && (
                      <Button variant="outline" onClick={handleClearCompletedTasks} className="w-full">
                        Clear Completed Tasks
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ringfences" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Ring Fences</CardTitle>
                      <CardDescription>Ring-fence funds for specific purposes</CardDescription>
                    </div>
                    <Dialog open={isRingFenceDialogOpen} onOpenChange={(open) => {
                      setIsRingFenceDialogOpen(open);
                      if (!open) setEditingRingFence(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button><Plus className="h-4 w-4 mr-2" />Create Ring Fence</Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>{editingRingFence ? 'Edit' : 'Create'} Ring Fence</DialogTitle>
                          <DialogDescription>
                            {editingRingFence ? 'Update' : 'Set aside'} funds for a specific purpose
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={editingRingFence ? handleUpdateRingFence : handleAddRingFence} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Title *</Label>
                            <Input name="title" defaultValue={editingRingFence?.title} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Details</Label>
                            <Textarea name="details" defaultValue={editingRingFence?.details || ''} rows={3} />
                          </div>
                          <div className="space-y-2">
                            <Label>Amount (£) *</Label>
                            <Input name="amount" type="number" step="0.01" defaultValue={editingRingFence?.amount} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Start Date *</Label>
                            <Input name="start_date" type="date" defaultValue={editingRingFence?.start_date} required />
                          </div>
                          <div className="space-y-2">
                            <Label>Expected End Date</Label>
                            <Input name="expected_end_date" type="date" defaultValue={editingRingFence?.expected_end_date || ''} />
                          </div>
                          {editingRingFence && (
                            <>
                              <div className="space-y-2">
                                <Label>Actual End Date</Label>
                                <Input name="actual_end_date" type="date" defaultValue={editingRingFence?.actual_end_date || ''} />
                              </div>
                              <div className="space-y-2">
                                <Label>Status</Label>
                                <Select name="status" defaultValue={editingRingFence?.status}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </>
                          )}
                          <div className="space-y-2">
                            <Label>Link to Goal (Optional)</Label>
                            <Select name="goal_id" defaultValue={editingRingFence?.goal_id || 'none'}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select goal" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {goals.map(g => (
                                  <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Link to Event (Optional)</Label>
                            <Select name="event_id" defaultValue={editingRingFence?.event_id || 'none'}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select event" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {events.map(e => (
                                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => {
                              setIsRingFenceDialogOpen(false);
                              setEditingRingFence(null);
                            }}>
                              Cancel
                            </Button>
                            <Button type="submit">{editingRingFence ? 'Update' : 'Create'}</Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  {ringFences.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No ring fences created yet</p>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {ringFences.map((rf) => (
                        <Card key={rf.id} className={rf.status === 'completed' ? 'opacity-60' : ''}>
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-base">{rf.title}</CardTitle>
                                <div className="text-2xl font-bold text-primary mt-2">
                                  £{rf.amount.toFixed(2)}
                                </div>
                                {rf.details && (
                                  <p className="text-sm text-muted-foreground mt-2">{rf.details}</p>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-2">
                            <div className="text-sm">
                              <span className="font-medium">Started:</span> {new Date(rf.start_date).toLocaleDateString()}
                            </div>
                            {rf.expected_end_date && (
                              <div className="text-sm">
                                <span className="font-medium">Expected End:</span> {new Date(rf.expected_end_date).toLocaleDateString()}
                              </div>
                            )}
                            {rf.actual_end_date && (
                              <div className="text-sm">
                                <span className="font-medium">Actual End:</span> {new Date(rf.actual_end_date).toLocaleDateString()}
                              </div>
                            )}
                            <div className="flex items-center gap-2 pt-2">
                              <Badge variant={rf.status === 'active' ? 'default' : rf.status === 'completed' ? 'secondary' : 'outline'}>
                                {rf.status}
                              </Badge>
                            </div>
                            <div className="flex gap-1 pt-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button size="sm" variant="ghost" onClick={() => {
                                      setEditingRingFence(rf);
                                      setIsRingFenceDialogOpen(true);
                                    }}>
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit ring fence</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button size="sm" variant="ghost" onClick={() => handleDeleteRingFence(rf.id)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete ring fence</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="space-y-3">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Reserve Amount</CardTitle>
                  <CardDescription>Set the minimum amount to keep in the bank account</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleUpdateReserve} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Reserve Amount (£)</Label>
                      <Input name="reserve_amount" type="number" step="0.01" defaultValue={reserveAmount} required />
                    </div>
                    <Button type="submit">Update Reserve</Button>
                  </form>
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Financial Year Settings</CardTitle>
                  <CardDescription>Manage financial year dates and archived records</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {currentFinancialYear && (
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium mb-2">Current Financial Year</h3>
                        <div className="flex gap-4 items-center">
                          <div className="flex-1">
                            <Label>Financial Year Period</Label>
                            <p className="text-lg font-semibold">
                              {formatFinancialYear(currentFinancialYear)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t">
                        <h3 className="font-medium mb-2">Update Financial Year Dates</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Set the recurring start and end dates for your financial year (day and month only)
                        </p>
                        <form onSubmit={handleUpdateFinancialYear} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Start Date (Day & Month)</Label>
                              <div className="flex gap-2">
                                <Select value={newYearStartDay} onValueChange={setNewYearStartDay} required>
                                  <SelectTrigger className="w-24">
                                    <SelectValue placeholder="Day" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                      <SelectItem key={day} value={day.toString()}>{day}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select value={newYearStartMonth} onValueChange={setNewYearStartMonth} required>
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Month" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">January</SelectItem>
                                    <SelectItem value="2">February</SelectItem>
                                    <SelectItem value="3">March</SelectItem>
                                    <SelectItem value="4">April</SelectItem>
                                    <SelectItem value="5">May</SelectItem>
                                    <SelectItem value="6">June</SelectItem>
                                    <SelectItem value="7">July</SelectItem>
                                    <SelectItem value="8">August</SelectItem>
                                    <SelectItem value="9">September</SelectItem>
                                    <SelectItem value="10">October</SelectItem>
                                    <SelectItem value="11">November</SelectItem>
                                    <SelectItem value="12">December</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label>End Date (Day & Month)</Label>
                              <div className="flex gap-2">
                                <Select value={newYearEndDay} onValueChange={setNewYearEndDay} required>
                                  <SelectTrigger className="w-24">
                                    <SelectValue placeholder="Day" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                      <SelectItem key={day} value={day.toString()}>{day}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Select value={newYearEndMonth} onValueChange={setNewYearEndMonth} required>
                                  <SelectTrigger className="flex-1">
                                    <SelectValue placeholder="Month" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">January</SelectItem>
                                    <SelectItem value="2">February</SelectItem>
                                    <SelectItem value="3">March</SelectItem>
                                    <SelectItem value="4">April</SelectItem>
                                    <SelectItem value="5">May</SelectItem>
                                    <SelectItem value="6">June</SelectItem>
                                    <SelectItem value="7">July</SelectItem>
                                    <SelectItem value="8">August</SelectItem>
                                    <SelectItem value="9">September</SelectItem>
                                    <SelectItem value="10">October</SelectItem>
                                    <SelectItem value="11">November</SelectItem>
                                    <SelectItem value="12">December</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                          <Button type="submit">Update Dates</Button>
                        </form>
                      </div>

                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Start New Financial Year Card */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Start New Financial Year</CardTitle>
                  <CardDescription>
                    Archive the current year and create a new one. All outstanding expenses must be paid first.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button onClick={handleStartNewYear} variant="default">
                    Start New Year
                  </Button>
                </CardContent>
              </Card>

              {/* Financial Years Card */}
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Financial Years</CardTitle>
                      <CardDescription>View and download financial year reports</CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowArchivedYears(!showArchivedYears)}
                    >
                      {showArchivedYears ? 'Hide' : 'Show'} Archived
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Current Year */}
                  {currentFinancialYear && (
                    <div className="border border-primary/30 bg-primary/5 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-semibold">
                            {formatFinancialYear(currentFinancialYear)}
                          </p>
                          <p className="text-sm text-primary font-medium">Current Year</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadFinancialYearPDF(currentFinancialYear)}
                          disabled={isGeneratingPDF}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Archived Years */}
                  {showArchivedYears && archivedYears.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-medium text-sm text-muted-foreground">Archived</h3>
                      {archivedYears.map((year) => (
                        <div
                          key={year.id}
                          className="border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium">
                                {formatFinancialYear(year)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadFinancialYearPDF(year)}
                              disabled={isGeneratingPDF}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Download PDF
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!currentFinancialYear && archivedYears.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No financial years configured yet
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Edit Alert Dialog - placed outside Tabs for global access */}
          <Dialog open={isEditAlertDialogOpen} onOpenChange={setIsEditAlertDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Alert</DialogTitle>
                <DialogDescription>Update alert details</DialogDescription>
              </DialogHeader>
              {editingAlert && (
                <form onSubmit={handleEditAlert} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Alert Title</Label>
                    <Input name="title" defaultValue={editingAlert.title} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input name="due_date" type="date" defaultValue={editingAlert.due_date?.split('T')[0]} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Alert Date</Label>
                    <Input name="alert_date" type="date" defaultValue={editingAlert.alert_date?.split('T')[0]} required />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsEditAlertDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Update Alert</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Task Dialog - placed outside Tabs for global access */}
          <Dialog open={isEditTaskDialogOpen} onOpenChange={setIsEditTaskDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Task</DialogTitle>
                <DialogDescription>Update task details</DialogDescription>
              </DialogHeader>
              {editingTask && (
                <form onSubmit={handleEditTask} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Task Title</Label>
                    <Input name="title" defaultValue={editingTask.title} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Description (Optional)</Label>
                    <Textarea name="description" defaultValue={editingTask.description || ''} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsEditTaskDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Update Task</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
  );
}
